import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { AnnualProcurementPlanModal } from '../components/AnnualProcurementPlanModal';
import { annualProcurementPlansApi, ppmpDepartmentsApi } from '../procurementPlansApi';
import type {
  AnnualProcurementPlanDetail,
  AnnualProcurementPlanListItem,
} from '../procurementPlanTypes';

/**
 * The annual procurement plans — one per fiscal year, the container every PPMP is built under.
 *
 * The **first work surface** in Procurement Planning: a reader goes here to do something, where
 * the three catalogs behind Settings are what the doing draws on.
 *
 * **It files by FISCAL YEAR**, which is read THROUGH the budget plan rather than off a column
 * here — the two can never drift, because this record has no year of its own.
 */
export function AnnualProcurementPlansIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<AnnualProcurementPlanDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    plan: AnnualProcurementPlanListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    TWO prerequisites, and they are different KINDS.

    A plan cannot be CREATED without an unframed budget plan — the form's only picker is fed
    from them, and one per plan means an organization whose every year is already framed has
    nothing to choose. That one BLOCKS.

    A plan with no enrolled department is creatable and USELESS: its roster is the whole of its
    profile, and with nobody enrolled it opens empty. That is `advisory` — named, and NOT
    disabling Create, because the next action does not wait on it.

    Both seeded MET, and a failed feed leaves them met: flashing "set this up first" on every
    load of a working module is worse than a banner arriving a moment late.
  */
  const [hasCandidates, setHasCandidates] = useState(true);
  const [hasDepartments, setHasDepartments] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    annualProcurementPlansApi
      .candidates(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setHasCandidates(rows.length > 0);
      })
      .catch(() => {
        // Leaves it met.
      });

    ppmpDepartmentsApi
      .list({ page: 1, pageSize: 1, status: 'active' }, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setHasDepartments(result.total > 0);
      })
      .catch(() => {
        // Leaves it met.
      });

    return () => controller.abort();
  }, [reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchPlans = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      annualProcurementPlansApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          fiscalYear: query.facets.fiscalYear?.join(','),
        },
        signal,
      ),
    [],
  );

  /** The edit form takes the DETAIL, which carries the window state the list row does not. */
  async function openEditor(plan: AnnualProcurementPlanListItem) {
    setError(null);

    try {
      setEditing(await annualProcurementPlansApi.get(plan.id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not open the plan.', 0));
    }
  }

  const rowActions = useCallback(
    (plan: AnnualProcurementPlanListItem): MenuItem[] => [
      {
        key: `edit-${plan.id}`,
        label: 'Edit',
        permission: 'annual-procurement-plans.update',
        onClick: () => void openEditor(plan),
      },
      {
        key: `restore-${plan.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'annual-procurement-plans.delete',
        disabledReason: plan.isActive ? 'This plan is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', plan }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await annualProcurementPlansApi.deactivate(confirming.plan.id);
      } else {
        await annualProcurementPlansApi.activate(confirming.plan.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<AnnualProcurementPlanListItem>
        module="annual-procurement-plans"
        breadcrumb={['Procurement', 'Procurement Planning', 'Annual Procurement Plan']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'fiscalYear', label: 'Fiscal year' },
        ]}
        /*
          FILES BY YEAR rather than status, which is the cut a reader takes first here — and the
          `All · Active · Inactive` strip would draw nothing anyway, being the one arrangement
          the shell suppresses. The reader can repoint it from the rail's cog if they disagree.
        */
        folderNavFacet="fiscalYear"
        auditEntityTypes={['AnnualProcurementPlan']}
        rowKey={(plan) => plan.id}
        fetch={fetchPlans}
        reloadKey={reloadKey}
        searchPlaceholder="Search plan numbers, budget plans, descriptions or notes"
        emptyMessage="No annual procurement plans yet. Create one against the budget plan it is framed by."
        prerequisites={[
          {
            label: 'a budget plan not already framed by one',
            met: hasCandidates,
            // The default sentence would be untrue: there are budget plans, and every one of
            // them is already framed. `met` asks the narrower question, so the page says so.
            detail:
              'Every budget plan already has an annual procurement plan. One plan frames one year.',
            to: '/procurement/investment-planning/budget-plans',
            permission: 'budget-plans.read',
          },
          {
            label: 'a PPMP department',
            met: hasDepartments,
            to: '/procurement/procurement-planning/settings/ppmp-departments',
            permission: 'ppmp-departments.read',
            /*
              ADVISORY — named, and it does NOT disable Create. A plan with nobody enrolled is
              perfectly creatable and then its roster, which is the whole of its profile, opens
              empty: every PPMP is filed from a roster row, so there is nowhere to file from.
              That is the module CRIPPLED rather than the form unsubmittable, which is the line
              this flag draws.
            */
            advisory:
              'Nobody is enrolled to file a PPMP, so every plan opens on an empty roster.',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="annual-procurement-plans.create"
        onRowOpen={(plan) =>
          navigate(`/procurement/procurement-planning/annual-procurement-plans/${plan.id}`)
        }
        openPermission="annual-procurement-plans.read"
        rowActions={rowActions}
        onDelete={(plan) => setConfirming({ kind: 'deactivate', plan })}
        deletePermission="annual-procurement-plans.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(plan) => (plan.isActive ? null : 'This plan is already deleted.')}
        onClearData={() => annualProcurementPlansApi.clear()}
        clearDataPermission="annual-procurement-plans.clear-data"
        otherInfo={(plan) => plan}
      />

      {creating || editing !== null ? (
        <AnnualProcurementPlanModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={
            confirming.kind === 'deactivate'
              ? 'Delete annual procurement plan'
              : 'Restore annual procurement plan'
          }
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`annual-procurement-plan-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `${confirming.plan.appNumber} is hidden from this list, and ${confirming.plan.budgetPlanNumber} becomes free for another procurement plan.`
                  : `${confirming.plan.appNumber} returns to this list.`}
              </p>

              {confirming.kind === 'deactivate' ? (
                <p>
                  {confirming.plan.ppmpCount === 0
                    ? 'Nothing has been filed under it.'
                    : `The ${confirming.plan.ppmpCount} PPMP${confirming.plan.ppmpCount === 1 ? '' : 's'} filed under it are kept exactly as they are, so a restore brings the plan back with its contents.`}{' '}
                  The plan NUMBER is never reused — a deleted plan keeps it, because every PPMP
                  quoting {confirming.plan.appNumber} must stay unambiguous.
                </p>
              ) : (
                <p>
                  Refused if another active plan has taken {confirming.plan.budgetPlanNumber}
                  meanwhile — a deleted plan frees its budget plan.
                </p>
              )}

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<AnnualProcurementPlanListItem>[] = [
  {
    key: 'appNumber',
    header: 'APP no.',
    width: 160,
    sortable: true,
    // A machine value: mono, never numeric — a code is an identifier, so nothing in it dims.
    render: (plan) => <TextCell value={plan.appNumber} mono />,
  },
  {
    key: 'fiscalYear',
    header: 'FY',
    width: 90,
    align: 'right',
    sortable: true,
    summary: true,
    // A calendar YEAR is an identifier, not a quantity — mono without numeric.
    render: (plan) => <TextCell value={String(plan.fiscalYear)} mono />,
  },
  {
    key: 'budgetPlanNumber',
    header: 'Budget plan',
    width: 150,
    summary: true,
    // Live, through the join. It is what a reader recognises the frame by.
    render: (plan) => <TextCell value={plan.budgetPlanNumber} mono />,
  },
  {
    key: 'submissionClosesAt',
    header: 'Submission by',
    width: 150,
    sortable: true,
    /*
      A CALENDAR date, so `formatDateOnly` through a mono TextCell — never `<DateCell>`, which
      renders an instant in the viewer's zone and would tell a reader west of UTC the day
      before the one on the form.
    */
    render: (plan) => <TextCell value={formatDateOnly(plan.submissionClosesAt)} mono />,
  },
  {
    key: 'coverageStartDate',
    header: 'Coverage from',
    width: 150,
    sortable: true,
    render: (plan) => <TextCell value={formatDateOnly(plan.coverageStartDate)} mono />,
  },
  {
    key: 'coverageEndDate',
    header: 'Coverage to',
    width: 150,
    render: (plan) => <TextCell value={formatDateOnly(plan.coverageEndDate)} mono />,
  },
  {
    key: 'ppmpCount',
    header: 'PPMPs',
    width: 100,
    align: 'right',
    // NOT sortable: a correlated count would be computed for every row before the page could
    // be taken, which is the call `BudgetDepartment.ppaCount` already makes.
    render: (plan) => <TextCell value={String(plan.ppmpCount)} mono numeric />,
  },
  {
    key: 'estimatedBudget',
    header: 'Estimated budget',
    width: 170,
    align: 'right',
    // Derived from the lines on every read. A quantity, so a zero dims.
    render: (plan) => <TextCell value={formatMoney(plan.estimatedBudget)} mono numeric />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    render: (plan) => <TextCell value={plan.description} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (plan) =>
      plan.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
